import React, {FunctionComponent, useEffect, useRef, useState} from 'react';
import {Modal} from 'react-bootstrap';
import cn from 'classnames';
import produce from 'immer';

import "./BulkDataModal.scss";
import {Button} from 'components';

interface IBulkDataModalProps {
    defaultLines?: string[],
    itemName: string,
    itemNamePlural: string,
    lineValidRegex: RegExp,
    onCancel: () => void,
    onSave: (locations: string[]) => void,
    readOnly?: boolean,
    show: boolean,
    subtitle: string,
    title: string,
}

interface ILineEntry {
    lineNum: number,
    value: string,
    key: number,
}

interface IDuplicateEntry {
    lineValue: string,
    lineNumbers: number[],
}

interface IBulkDataModalState {
    duplicateLines: IDuplicateEntry[],
    invalidLines: ILineEntry[],
    linesCount: number,
    // The textarea is an uncontrolled input for performance. Sometimes, we want to reset its value.
    // To do that, we'll increment a key prop, which will make react rerender it as a new component.
    textareaDefaultVal: string,
    textareaKey: number,
}

export const BulkDataModal: FunctionComponent<IBulkDataModalProps> = function ({
    defaultLines, itemName, itemNamePlural, lineValidRegex, onCancel, onSave, readOnly, show, subtitle, title
}) {
    const textareaRef = useRef<HTMLTextAreaElement>();
    const [state, setState] = useState<IBulkDataModalState>({
        duplicateLines: [],
        invalidLines: [],
        linesCount: 0,
        textareaDefaultVal: '',
        textareaKey: 1,
    })
    const {duplicateLines, invalidLines, linesCount, textareaDefaultVal, textareaKey} = state;
    const showError = (duplicateLines.length > 0 || invalidLines.length > 0);
    defaultLines = defaultLines || [];

    useEffect(() => {
        // Reset our state when we're shown. Bootstrap modals are persistent,
        // so we don't get reset if the user closes then reopens.
        if (show) {
            setTextAreaState({
                lines: defaultLines,
                invalid: [],
                dupes: [],
            });
        }
    }, [show]);

    function createTextareaString(textareaLocations: string[]): string {
        return textareaLocations.join(`,\n`);
    }

    // Parse the textarea's value into an array of lines.
    function getLines(): string[] {
        if (!textareaRef.current) return [];

        const val = textareaRef.current.value;

        // First, split by commas or whitespace.
        // Regex:
        //     \s*     optional whitespace
        //     [,\s]   comma or whitespace
        //     \s*     optional whitespace
        let lines = val.split(/\s*[,\s]\s*/)

        // Trim each line, then remove empty lines
        lines = lines.map(line => line.trim())
        lines = lines.filter(line => line.length > 0)
        return lines;
    }

    // Validate each line and check for duplicates. This needs to be performant for up to 50k lines,
    // so we're using O(n) algorithms.
    function checkLines(lines: string[]): {
        dupes: IDuplicateEntry[],
        invalid: ILineEntry[]
    } {
        let invalid: ILineEntry[] = [];

        // Calculate duplicates - we'll create a map with the line's value as the key for O(1) lookups.
        const existingLines: {[lineVal: string]: ILineEntry[]} = {};

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const lineEntry: ILineEntry = {
                lineNum: i,
                value: line,
                key: i,
            }

            if (!line.match(lineValidRegex)) {
                invalid.push(lineEntry);
            }

            if (!existingLines[line]) {
                // No existing lines so far, not a duplicate yet.
                existingLines[line] = [lineEntry];
            } else {
                // Duplicate detected, add this line to the list.
                existingLines[line].push(lineEntry);
            }
        }

        // Compile an array of all duplicates.
        let dupes: IDuplicateEntry[] = [];
        for (const linesArr of Object.values(existingLines)) {
            if (linesArr.length > 1) {
                dupes.push({
                    lineValue: linesArr[0].value,
                    lineNumbers: linesArr.map(l => l.lineNum),
                });
            }
        }

        return {
            dupes: dupes,
            invalid: invalid,
        }
    }

    // This helper function makes the textarea rerender. It also keeps the linesCount updated.
    function setTextAreaState(params: {
        lines: string[],
        invalid?: ILineEntry[],
        dupes?: IDuplicateEntry[],
    }) {
        setState(produce(draft => {
            draft.linesCount = params.lines.length;
            // Update the textarea to show our current state. We'll bump its key to make it rerender.
            draft.textareaDefaultVal = createTextareaString(params.lines);
            draft.textareaKey++;
            // Show any errors
            if (params.dupes) {
                draft.duplicateLines = params.dupes;
            }
            if (params.invalid) {
                draft.invalidLines = params.invalid;
            }
        }))
    }

    function validateAndShowErrors(lines: string[]): {valid: boolean} {
        const {dupes, invalid} = checkLines(lines);
        setTextAreaState({
            lines: lines, 
            dupes: dupes,
            invalid: invalid,
        });
        return {valid: dupes.length === 0 && invalid.length === 0};
    }

    function onRemoveInvalid() {
        let lines = getLines();
        const {invalid} = checkLines(lines);

        // Remove invalid lines. First create a map of invalid lines for O(1) lookups
        const invalidLineNums: {[lineNum: number]: boolean} = {};

        for (const line of invalid) {
            invalidLineNums[line.lineNum] = true;
        }

        // To be included, a line's index must not be in the invalid line nums map.
        lines = lines.filter((line, i) => {
            return !invalidLineNums[i];
        });

        // Now revalidate, because we might have removed invalid duplicates.
        const {invalid: invalid2, dupes: dupes2} = checkLines(lines);

        setTextAreaState({
            lines: lines,
            invalid: invalid2,
            dupes: dupes2,
        });
    }

    function onRemoveDuplicates() {
        let lines = getLines();

        // Remove duplicates. Create map of existing for fast lookups
        const existing: {[lineVal: string]: boolean} = {};

        lines = lines.filter((line) => {
            if (!existing[line]) {
                // Line has not been added yet - mark it as existing and then include it.
                existing[line] = true;
                return true;
            }
            // Line already exists, so this is a duplicate that should be excluded.
            return false;
        });

        // Now revalidate because we might have removed some invalid duplicates.
        const {invalid: invalid2, dupes: dupes2} = checkLines(lines);

        setTextAreaState({
            lines: lines,
            invalid: invalid2,
            dupes: dupes2,
        });
    }

    function onValidate() {
        const lines = getLines();
        validateAndShowErrors(lines);
    }

    function onSaveInternal() {
        const lines = getLines();
        const {valid} = validateAndShowErrors(lines);
        
        if (valid) {
            onSave(lines);
        }
    }

    function pluralizeItem(num: number): string {
        return num === 1 ? itemName : itemNamePlural;
    }

    // There could be up to 50k invalid lines. We can't render that, so we'll limit
    // how many errors we display.
    const errorsLimit = 100;
    const invalidLinesLimited = invalidLines.slice(0, errorsLimit);
    const duplicatesLimited = duplicateLines.slice(0, errorsLimit);
    const didLimitInvalid = invalidLinesLimited.length !== invalidLines.length;
    const didLimitDuplicates = duplicatesLimited.length !== duplicateLines.length;

    return (
        <Modal show={show} onHide={onCancel}>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="bulkDataModal_subtitle">
                    {subtitle}
                </p>
                <textarea
                    className={cn("bulkDataModal_textarea", {
                        'invalid': showError,
                    })}
                    defaultValue={textareaDefaultVal}
                    rows={5}
                    key={textareaKey}
                    ref={textareaRef}
                    readOnly={readOnly}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                ></textarea>
                <div className="bulkDataModal_count">
                    {linesCount} total {itemNamePlural}.
                </div>
                {invalidLines.length > 0 && (
                    <div className="bulkDataModal_errors">
                        <div className="bulkDataModal_errorHeader">
                            {invalidLines.length} invalid {pluralizeItem(invalidLines.length)}:
                        </div>
                        <ul className="bulkDataModal_errorsList">
                            {invalidLinesLimited.map(l => (
                                <li key={l.key} className="bulkDataModal_errorItem">
                                    <span className="bulkDataModal_errorLine">Line {l.lineNum + 1}:</span>
                                    {l.value}
                                </li>
                            ))}
                            {didLimitInvalid && (
                                <li key="limitReached">
                                    And {invalidLines.length - invalidLinesLimited.length} more...
                                </li>
                            )}
                        </ul>
                        <div className="bulkDataModal_errorButtons">
                            <Button theme="blueOutline" size="small" onClick={onRemoveInvalid}>
                                Remove Invalid Lines
                            </Button>
                        </div>
                    </div>
                )}
                {duplicateLines.length > 0 && (
                    <div className="bulkDataModal_errors">
                        <div className="bulkDataModal_errorHeader">
                            {duplicateLines.length} {pluralizeItem(duplicateLines.length)} with duplicates:
                        </div>
                        <ul className="bulkDataModal_errorsList">
                            {duplicatesLimited.map(dupe => (
                                <li key={dupe.lineValue}>
                                    <span className="bulkDataModal_errorLine">{dupe.lineValue}</span>
                                    <ul className="bulkDataModal_duplicatesList">
                                        {dupe.lineNumbers.map(lineNum => (
                                            <li key={lineNum}>
                                                Line {lineNum + 1}
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            ))}
                            {didLimitDuplicates && (
                                <li key="limitReached">
                                    And {duplicateLines.length - duplicatesLimited.length} more...
                                </li>
                            )}
                        </ul>
                        <div className="bulkDataModal_errorButtons">
                            <Button theme="blueOutline" size="small" onClick={onRemoveDuplicates}>
                                Remove Duplicates
                            </Button>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                {readOnly && (<>
                    <Button theme="red" onClick={onCancel}>
                        Ok
                    </Button>
                </>)}
                {!readOnly && (<>    
                    <Button theme="text" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button theme="blueOutline" onClick={onValidate}>
                        Validate
                    </Button>
                    <Button theme="red" onClick={onSaveInternal}>
                        Confirm
                    </Button>
                </>)}
            </Modal.Footer>
        </Modal>
    );
}
